import { queryOptions, useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { HttpService } from '@Services/index';
import type {
  AccountantPermissionLevel,
  AuthorizedUserOnboardingType,
} from '@Store/slices/authOnboarding/authOnboarding.types';

export interface ValidateInviteResponse {
  authorizedUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  firmName: string;
  phoneNumber: string | null;
  invitationStatus: string;
  onboardingStatus: string;
  isExistingUser: boolean;
  existingUserName: string | null;
  onboardingType?: AuthorizedUserOnboardingType;
  accountantPermissionLevel?: AccountantPermissionLevel;
}

interface StartOnboardingRequest {
  phoneNumber: string;
  phoneNumberCountryCode?: string;
}

export const validateAccountantInvite = async (authorizedUserId: string) => {
  const { data } = await HttpService.get<ValidateInviteResponse>(
    `v3/onboarding/authorized/user/${authorizedUserId}`
  );
  return data;
};

export const getValidateAccountantInviteQueryOptions = (authorizedUserId: string) =>
  queryOptions({
    queryKey: ['accountantPortal', 'validateInvite', authorizedUserId],
    queryFn: () => validateAccountantInvite(authorizedUserId),
  });

export const startAccountantOnboarding = async (
  authorizedUserId: string,
  data: StartOnboardingRequest
) => {
  await HttpService.post(`v3/onboarding/authorized/user/${authorizedUserId}/start`, data);
};

export const useStartAccountantOnboarding = (
  options?: UseMutationOptions<void, Error, { authorizedUserId: string; phoneNumber: string }>
) =>
  useMutation({
    mutationFn: ({
      authorizedUserId,
      phoneNumber,
    }: {
      authorizedUserId: string;
      phoneNumber: string;
    }) => startAccountantOnboarding(authorizedUserId, { phoneNumber }),
    ...options,
  });
